import { Component, OnInit, ChangeDetectionStrategy, input, signal, computed } from '@angular/core';
import { ChatAdminService } from '../../services/chat-admin.service';
import { Subscription, filter, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ScrollingService } from '../../services/scrolling.service';
import { ChatCloudLinkService } from '../../services/chat-cloudlink.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedHelpers } from '../../classes/sharedhelpers';
import { ChatConversationService } from '../../services/chat-conversation.service';

@Component({
  selector: 'mi-mention',
  templateUrl: './mention.component.html',
  styleUrls: ['./mention.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MentionComponent implements OnInit {
  // Input signal
  userId = input.required<string>();

  // Internal state signals
  foundDisplayName = signal(false);
  isCustomElement = signal(false);
  disableTooltip = signal(false);
  loaded = signal(false);

  // Observable for external consumers (forkJoin compatibility)
  loaded$ = toObservable(this.loaded).pipe(
    filter(loaded => loaded),
    take(1)
  );

  // Computed signals for template
  displayName = computed(() => {
    if (this.userId() === 'here') {
      return this.translate.instant('cl_chat.here');
    } else if (this.foundDisplayName() && this.chatAdminService.displayNames[this.userId()]) {
      return this.chatAdminService.displayNames[this.userId()];
    } else if (this.loaded()) {
      return this.userId();
    }
    return '';
  });

  email = computed(() => {
    if (this.foundDisplayName()) {
      return this.chatAdminService.emails[this.userId()] ? this.chatAdminService.emails[this.userId()] : '';
    }
    return '';
  });

  constructor(
    public chatAdminService: ChatAdminService,
    public scrollingService: ScrollingService,
    private chatCloudlinkService: ChatCloudLinkService,
    private chatConversationService: ChatConversationService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.isCustomElement.set(!!customElements.get('cloudlink-chat'));
    if (this.userId() === 'here') {
      this.foundDisplayName.set(true);
      this.disableTooltip.set(true);
      this.loaded.set(true);
    } else if (this.chatCloudlinkService.offlineLogin) {
      await this.loadMention(true);
    } else {
      //Load from cache
      if (
        this.chatAdminService.displayNames[this.userId()] &&
        (this.chatAdminService.displayNames[this.userId()] !== this.translate.instant('cl_chat.unknownUser') ||
          SharedHelpers.guidRegex.test(this.userId()))
      ) {
        this.loadMention(true);
        this.chatAdminService.getPrincipal(this.userId());
      } else {
        await this.chatAdminService.getPrincipal(this.userId());
        if (this.chatAdminService.usersLoaded$[this.userId()]) {
          var userLoadedSub = new Subscription();
          userLoadedSub.add(
            this.chatAdminService.usersLoaded$[this.userId()].subscribe(async () => {
              userLoadedSub.unsubscribe();
              await this.loadMention();
            })
          );
        }
      }
    }
  }

  async loadMention(fromOffline = false): Promise<void> {
    if (fromOffline) {
      if (this.chatAdminService.displayNames[this.userId()]) {
        this.foundDisplayName.set(true);
      }
    } else {
      var principal = await this.chatAdminService.getPrincipal(this.userId());
      if (principal || SharedHelpers.guidRegex.test(this.userId())) {
        this.foundDisplayName.set(true);
      }
    }
    if (this.scrollingService.isNearBottom()) {
      setTimeout(() => {
        this.scrollingService.scrollToBottom();
      }, 0);
    }
    this.loaded.set(true);
  }

  getDisplayName() {
    return this.displayName();
  }

  getEmail() {
    return this.email();
  }

  mentionClick() {
    if (this.foundDisplayName() && !this.disableTooltip()) {
      this.chatConversationService.mentionClicked$.next(this.userId());
    }
  }
}
